/**
 * Quién puede gestionar el catálogo de usuarios (lista, altas, edición completa).
 * Alineado con API (`UserRoleDelegation.CanManageUsers`) y JWT (Admin / ADMIN / SUPERADMIN).
 */
export function normalizeRole(role: string | null | undefined): string {
  return String(role ?? '').trim().toLowerCase();
}

export function hasAnyRole(roles: string[] | undefined, allowed: string[]): boolean {
  const allowedSet = new Set(allowed.map(normalizeRole));
  return (roles ?? []).some((r) => allowedSet.has(normalizeRole(r)));
}

export function canManageSistemaUsers(roles: string[] | undefined): boolean {
  return hasAnyRole(roles, ['superadmin', 'admin']);
}

export function isSuperAdminRole(roles: string[] | undefined): boolean {
  return hasAnyRole(roles, ['superadmin']);
}

/** Catálogo global de tenants: exclusivo SUPERADMIN (MasterDB). */
export function canManageTenants(roles: string[] | undefined): boolean {
  return isSuperAdminRole(roles);
}

export function canManageVisitas(roles: string[] | undefined): boolean {
  return hasAnyRole(roles, ['admin', 'superadmin', 'gerente']);
}

export function canManageIntegrationAdmin(roles: string[] | undefined): boolean {
  return hasAnyRole(roles, ['admin', 'superadmin']);
}

/** Roles persistidos en login; evita flash de permisos antes de rehidratar `user` en AuthContext. */
export function getRolesFromSessionStorage(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem('roles');
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function effectiveUserRoles(rolesFromContext: string[] | undefined): string[] {
  if (rolesFromContext && rolesFromContext.length > 0) return rolesFromContext;
  return getRolesFromSessionStorage();
}

export function getUsernameFromSessionStorage(): string {
  if (typeof window === 'undefined') return '';
  const u =
    localStorage.getItem('username') ||
    localStorage.getItem('userName') ||
    localStorage.getItem('user') ||
    '';
  return u.trim();
}

export function effectiveUsername(usernameFromContext: string | undefined): string {
  const u = usernameFromContext?.trim();
  if (u) return u;
  return getUsernameFromSessionStorage();
}
